import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FlexLayoutModule } from '@angular/flex-layout';
import { JwtModule, JwtModuleOptions } from '@auth0/angular-jwt';
import { LayoutModule } from '@angular/cdk/layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatSortModule } from '@angular/material/sort';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ModCanchasComponent } from './components/mod-canchas/mod-canchas.component';
import { ModReservasComponent } from './components/mod-reservas/mod-reservas.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { ReservaComponent } from './components/reserva/reserva.component';
import { ReservasCanchaComponent } from './components/reservas-cancha/reservas-cancha.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { LoginComponent } from './components/login/login.component';
import { ReservaDialogComponent } from './components/reserva-dialog/reserva-dialog.component';
import { ModSorteosComponent } from './components/mod-sorteos/mod-sorteos.component';
import { ReservaModalComponent } from './components/reserva-modal/reserva-modal.component';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { LogoutComponent } from './components/login/logout.component';
import { AuthInterceptor } from './_helpers/auth.interceptor';
import { ModPlayersComponent } from './components/mod-players/mod-players.component';
import { PlayerComponent } from './components/player/player.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { ModReservasListComponent } from './components/mod-reservas-list/mod-reservas-list.component';
import { FeedbackMain } from './components/feedback/main/feedbackMain.component';
import { RatingSticker } from './components/shared/ratingSticker/ratingSticker.component';
import { FeedbackDialog } from './components/feedback/feedback-dialog/feedbackDialog.component';

// Giveaways
import { GiveawayDetailComponent } from './components/mod-sorteos/giveaway-detail/giveaway-detail.component';
import { GiveawaysListComponent } from './components/mod-sorteos/giveaways-list/giveaways-list.component';
import { GiveawayNewPrizeComponent } from './components/mod-sorteos/giveaway-new-prize/giveaway-new-prize.component';
import { GiveawayDeliverPrizeComponent } from './components/mod-sorteos/giveaway-deliver-prize/giveaway-deliver-prize.component';

// Health
import { HealthComponent } from './components/health/health-list.component';
import { HealthDetailComponent } from './components/health/health-detail/health-detail.component';
import { HealthAddTagComponent } from './components/health/health-add-tag/health-add-tag.component';
import { HealthAddPromoterComponent } from './components/health/health-add-promoter/health-add-promoter.component';

// Tournaments
import { TournamentsListComponent } from './components/tournaments-list/tournaments-list.component';
import { CreateTournamentComponent } from './components/tournaments-list/create-tournament/create-tournament.component';
import { TournamentDetailComponent } from './components/tournaments-list/tournament-detail/tournament-detail.component';
import { TournamentDetailTeamsComponent } from './components/tournaments-list/tournament-detail/tournament-detail-teams/tournament-detail-teams.component';
import { TournamentDetailCreateTeamComponent } from './components/tournaments-list/tournament-detail/tournament-detail-create-team/tournament-detail-create-team.component';
import { TournamentFixtureComponent } from './components/tournaments-list/tournament-detail/tournament-fixture/tournament-fixture.component';
import { TournamentFixtureMatchResultComponent } from './components/tournaments-list/tournament-detail/tournament-fixture/tournament-fixture-match-result/tournament-fixture-match-result.component';
import { TournamentPositionsComponent } from './components/tournaments-list/tournament-detail/tournament-positions/tournament-positions.component';

// Shared
import { AddSponsorComponent } from './components/shared/add-sponsor/add-sponsor.component';
import { ReservaRecurrenteModalComponent } from './components/reserva-recurrente-modal/reserva-recurrente-modal.component';

// Token getter
export function tokenGetter(): string | null{
  return sessionStorage.getItem('access_token');
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'dddd DD-MMM-YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMM YYYY',
  },
};

@NgModule({
  declarations: [
    AppComponent,
    ModCanchasComponent,
    ModReservasComponent,
    ModPlayersComponent,
    NavbarComponent,
    ReservaComponent,
    ReservasCanchaComponent,
    TopbarComponent,
    LoginComponent,
    LogoutComponent,
    ReservaDialogComponent,
    ModSorteosComponent,
    ReservaModalComponent,
    PlayerComponent,
    ConfirmDialogComponent,
    ModReservasListComponent,
    FeedbackMain,
    RatingSticker,
    FeedbackDialog,
    GiveawayDetailComponent,
    GiveawayNewPrizeComponent,
    AddSponsorComponent,
    GiveawaysListComponent,
    HealthComponent,
    HealthDetailComponent,
    HealthAddTagComponent,
    GiveawayDeliverPrizeComponent,
    HealthAddPromoterComponent,
    TournamentsListComponent,
    CreateTournamentComponent,
    TournamentDetailComponent,
    TournamentDetailTeamsComponent,
    TournamentDetailCreateTeamComponent,
    TournamentFixtureComponent,
    TournamentPositionsComponent,
    TournamentFixtureMatchResultComponent,
    ReservaRecurrenteModalComponent,
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    FlexLayoutModule,
    JwtModule.forRoot({
      config: {
        tokenGetter,
        whitelistedDomains: ['localhost:8000'],
      },
    }),
    LayoutModule,
    MatToolbarModule,
    MatIconModule,
    MatListModule,
    MatChipsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSidenavModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSortModule,
    MatSnackBarModule,
    MatCardModule,
    MatTableModule,
    MatPaginatorModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatGridListModule,
    MatTabsModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatButtonToggleModule,
    MatMomentDateModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    DragDropModule,
  ],
  providers: [
    MatNativeDateModule,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: [] },
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    ReservaDialogComponent,
    ReservaModalComponent,
    ConfirmDialogComponent,
    FeedbackDialog,
    GiveawayNewPrizeComponent,
    AddSponsorComponent,
    GiveawayDeliverPrizeComponent,
    HealthAddTagComponent,
    HealthAddPromoterComponent,
    TournamentDetailCreateTeamComponent,
    TournamentFixtureMatchResultComponent,
    ReservaRecurrenteModalComponent,
  ]
})
export class AppModule { }
